import React, { useEffect, useState } from 'react'
import { useOrgStore } from '../../stores/orgStore'
import { OrgService } from '../../services/OrgService'
import { Input } from '../ui/Input/Input'
import { Button } from '../ui/Button/Button'
import { usePageHeader } from '@/hooks/usePageHeader'
import { t } from '@/lib/i18n'

interface MemberRow { user_id: string; email: string; role: 'owner'|'admin'|'member' }

export function MembersManager() {
  usePageHeader({ title: t('Members'), breadcrumbs: [] })
  const orgId = useOrgStore(s => s.activeOrgId)
  const [members, setMembers] = useState<MemberRow[]>([])
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<'admin'|'member'>('member')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const svc = new OrgService()

  const load = async () => {
    if (!orgId) return
    try {
      setLoading(true)
      const rows = await svc.listMembers(orgId)
      setMembers(rows)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load members')
    } finally { setLoading(false) }
  }

  useEffect(() => { load() }, [orgId])

  const invite = async () => {
    if (!orgId) return
    try {
      setLoading(true)
      await svc.invite(orgId, email.trim().toLowerCase(), [role])
      setEmail('')
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to invite')
    } finally { setLoading(false) }
  }

  if (!orgId) return <div className="text-sm text-muted-foreground">No organization selected.</div>

  return (
    <div className="p-4">
      <h2 className="text-lg font-semibold mb-3">Members</h2>
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <Input
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="Invite by email"
          aria-label="Invite by email"
        />
        <select className="border rounded px-2 py-1 text-sm" value={role} onChange={e => setRole(e.target.value as any)}>
          <option value="member">Member</option>
          <option value="admin">Admin</option>
        </select>
        <Button size="sm" onClick={invite} disabled={loading || !email}>Invite</Button>
      </div>
      {error && <div className="text-destructive text-sm mb-2">{error}</div>}
      <div className="divide-y border rounded">
        {members.map(m => (
          <div key={m.user_id} className="flex items-center justify-between px-3 py-2">
            <div>
              <div className="text-sm font-medium">{m.email}</div>
              <div className="text-xs text-muted-foreground">{m.role}</div>
            </div>
            <div className="flex items-center gap-2">
              <select
                className="border rounded px-2 py-1 text-xs"
                value={m.role}
                onChange={async (e) => {
                  if (!orgId) return
                  const newRole = e.target.value as 'owner'|'admin'|'member'
                  try {
                    setLoading(true)
                    await svc.updateMemberRole(orgId, m.user_id, newRole)
                    await load()
                  } finally { setLoading(false) }
                }}
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
                <option value="owner">Owner</option>
              </select>
              <Button size="sm" variant="destructive" onClick={async () => {
                if (!orgId) return
                try {
                  setLoading(true)
                  await svc.removeMember(orgId, m.user_id)
                  await load()
                } finally { setLoading(false) }
              }}>Remove</Button>
            </div>
          </div>
        ))}
        {members.length === 0 && (
          <div className="p-3 text-sm text-muted-foreground">No members yet.</div>
        )}
      </div>
    </div>
  )
}


